import Button from "@shared/components/Atoms/Button/Button"
import { IconNames } from "@shared/enums/icons.enums"
import S from "./Contacts.module.scss"
import { useTranslations } from "@shared/context/useTranslations";
import { ModalButtonType, ModalSize, useModal } from "@shared/context/ModalContext";
import { useToast } from "@shared/context/ToastContext";
import { useContact } from "@shared/context/ContactContext";
import { ContactEdit, ContactEditRef } from "@shared/components/Molecules/ContactEdit/ContactEdit.component";
import { useEffect, useRef, useState } from "react";
import { Contact } from "@shared/types/UserAccountTypes.interface";
import { SvgIcon } from "@shared/components/Atoms/SvgIcon/SvgIcon";
import EmptyState from "@shared/components/Atoms/EmptyState/EmptyState.component";
import { cn } from "@shared/helpers/classNames.helper";
import { Chip } from "@shared/components/Atoms/Chip/Chip.component";


export const Contacts = () => {
    const { contacts, blank, updateContacts } = useContact();
    const { getTranslation } = useTranslations();
    const { showModal, closeModal } = useModal();
    const { showToast } = useToast();

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

    const ref = useRef<ContactEditRef>(null);
    const inputContactRef = useRef<Contact>();
    
    const openModal = (mode: "create" | "edit") => {
        showModal(
            <div>
                <ContactEdit
                    ref={ref}
                    allowPrimaryContact={true}
                    contact={inputContactRef.current!}
                />
            </div>
            ,
            {
                size: ModalSize.SM,
                shouldCloseOnOverlayClick: true,
                shouldCloseOnEsc: true,
                shouldCloseOnAction: false,
                title: `${getTranslation(mode === "edit" ? "Contacts.editContact" : "Contacts.createContact")}`,
                actionButton: {
                    label: `${getTranslation(mode === "edit" ? "button.save" : "button.create")}`,
                    type: mode === "edit" ? ModalButtonType.SUBMIT : ModalButtonType.SAVE,
                    onClick: () => handleSaveContact(mode),
                },
                cancelButton: {
                    label: `${getTranslation("button.cancel")}`,
                    onClick: handleCancelEditContact,
                },
            }
        );
    }

    const handleCancelEditContact = () => {
        ref.current?.reset();
        closeModal();
    }

    const handleEditItemClick = () => {
        if (selectedContact) {
            inputContactRef.current = selectedContact;
        }
        openModal("edit");
    }

    const handleSaveContact = (mode: "create" | "edit") => {
        const validContact = ref.current?.validate();

        if (validContact?.isValid && validContact.contact) {
            let list:Contact[];

            if (mode === "create") {
                list = [...contacts || [], validContact.contact];
            } else {
                list = contacts?.map(item => {
                    return item.id === validContact.contact?.id ? validContact.contact! : item
                }) || []
            }
            
            if (validContact.contact.isPrimary) {
                // If the contact being saved is marked as primary, we need to ensure that all other contacts are not marked as primary
                list = [...list.map(c => ({
                    ...c,
                    isPrimary: c.id === validContact.contact!.id
                }))];
            }            
            
            if (list) {
                updateContacts(list);
            }

            closeModal();
            showToast(
                <>
                    {getTranslation("contacts.toastMessage.success")}
                </>,
                IconNames.Checkmark
            );
        } else {
            console.error('Something went wrong')
        }
    };

    const handleCreate = () => {
        inputContactRef.current = blank;
        openModal("create");
    };

    const handleListClick = (contactId: string) => {
        setSelectedId(contactId);
    };

    const handleDeleteItemClick = () => {
        if (selectedContact) {
            handleConfirmDelete();
        }
    }

    const handleDeleteContact = () => {
        if (selectedContact && contacts) {
            const list = [...contacts.filter(c => c.id !== selectedContact.id)];
            updateContacts(list);
        }
    }    

    const handleConfirmDelete = () => {
        showModal(
            <div>
                {getTranslation("contacts.confirmDeleteContact")}
            </div>,
            {
                size: ModalSize.SM,
                shouldCloseOnOverlayClick: true,
                shouldCloseOnEsc: true,
                shouldCloseOnAction: true,
                title: getTranslation("contacts.confirmDeleteContactTitle"),
                actionButton: {
                    label: `${getTranslation("button.delete")}`,
                    type:  ModalButtonType.DELETE,
                    onClick: handleDeleteContact,
                },
                cancelButton: {
                    label: `${getTranslation("button.cancel")}`,
                    onClick: () => closeModal(),
                }
            }
        )
    }

    useEffect(() => {
        const contact = contacts?.find(c => c.id === selectedId) || contacts?.[0] || null;
        setSelectedContact(contact);
        inputContactRef.current = contact || undefined;
    }, [selectedId, contacts]);
   
    return (
        <div className={S.wrapper}>
            <div className={S.contactsList}>
                <Button
                    iconName={IconNames.Plus}
                    type="submit"
                    variant="primary"
                    iconPlacement="left"
                    className={S.addNewBtn}
                    onClick={handleCreate}
                >
                    {getTranslation("contacts.newContact")}
                </Button>

                <div className={S.listBox}>
                    {contacts && contacts?.length > 0 ? (
                        contacts?.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)).map((contact) => (
                            <div
                                key={contact.id}
                                className={cn(S.listBoxItem, {
                                    [S.listBoxItemActive]: contact.id === selectedId
                                })}
                                onClick={() => handleListClick(contact.id || "")}
                            >
                                <div className={S.listBoxItemTitle}>{contact.firstName} {contact.lastName}</div>
                                <span className={S.listBoxItemSubtitle}>
                                    {contact.email}
                                </span>

                                {contact.id === selectedId && (
                                    <span className={S.activeArrowIcon}>
                                        <SvgIcon iconName={IconNames.Smallarrowright} />
                                    </span>
                                )}

                                {contact.isPrimary && (
                                    <div>
                                        <Chip className={S.chip} label={getTranslation("contacts.primaryContact")} />
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <EmptyState heading={getTranslation("contacts.noItemsAvailable")} />
                    )}
                </div>
            </div>

            <div>
                <h1>{getTranslation("contacts.pageTitle")}</h1>
                {selectedContact ? (
                    <div className={S.selectedBox}>
                        <div className={S.selectedBoxHeader}>
                            <h3 className={S.selectedBoxTitle}>{selectedContact.firstName} {selectedContact.lastName}</h3>
                            <div className={S.selectedBoxHeaderButtons}>
                                <Button
                                    variant="full-transparent"
                                    className={S.titleEditBtn}
                                    onClick={handleEditItemClick}
                                >
                                    <SvgIcon iconName={IconNames.Pencil} />
                                </Button>
                                <Button
                                    variant="full-transparent"
                                    className={S.titleDeleteBtn}
                                    onClick={handleDeleteItemClick}
                                >
                                    <SvgIcon iconName={IconNames.Trashcan} />
                                </Button>
                            </div>
                        </div>
                        <div className={S.selectedBoxInfo}>
                            {selectedContact.email}
                        </div>
                        <div className={S.selectedBoxInfoSmall}>
                            {selectedContact.phone}
                        </div>
            
                        {selectedContact.isPrimary && (
                            <Chip className={S.chip} label={getTranslation("contacts.primaryContact")} />
                        )}
                    </div>
                ) : (
                    <p>{getTranslation("contacts.noContactSelected")}</p>
                )}
            </div>
        </div>
    )
}
